/* eslint-disable no-void */
/* eslint-disable @typescript-eslint/no-use-before-define */
import type { DocumentData, DocumentReference, Firestore } from 'firebase/firestore'
import { disableNetwork, enableNetwork, getDocFromServer } from 'firebase/firestore'

export const FIREBASE_WATCHDOG_EVENT_NAMES = Object.freeze({
  CONNECTION: 'firebase-watchdog:connection',
  MONITOR: 'firebase-watchdog:monitor',
  REQUEST_STATE: 'firebase-watchdog:request-state',
})

export const FIREBASE_WATCHDOG_BRIDGE_NAMES = Object.freeze({
  DEFAULT: 'firebaseWatchdog',
  LEGACY_DEMO_ALIAS: 'demoFirebaseWatchdog',
})

export const FIREBASE_WATCHDOG_SOURCE_NAMES = Object.freeze({
  FIRESTORE: 'firebase-firestore',
  FIRESTORE_PROBE_ERROR: 'firebase-firestore-probe-error',
})

const ERROR_CODES = Object.freeze({
  CANCELLED: 'cancelled',
  DEADLINE_EXCEEDED: 'deadline-exceeded',
  UNAVAILABLE: 'unavailable',
})

const TIMER_MS = Object.freeze({
  DEFAULT_PROBE_INTERVAL: 30000,
  DEFAULT_PROBE_TIMEOUT: 10000,
  RECONNECT_SETTLE: 250,
})

type WatchdogTarget = Window & typeof globalThis

interface FirestoreWatchdogBridge {
  goOffline: () => Promise<void>
  goOnline: () => Promise<void>
  reconnect: () => Promise<void>
}

interface InstallFirestoreConnectionWatchdogOptions {
  bridgeName?: string
  exposeLegacyDemoAlias?: boolean
  exposePageBridge?: boolean
  healthCheckRef: DocumentReference<DocumentData>
  probeIntervalMs?: number
  probeTimeoutMs?: number
  target?: WatchdogTarget
}

declare global {
  interface Window {
    demoFirebaseWatchdog?: FirestoreWatchdogBridge
    firebaseWatchdog?: FirestoreWatchdogBridge
  }
}

export function installFirestoreConnectionWatchdog(
  firestore: Firestore,
  options: InstallFirestoreConnectionWatchdogOptions
) {
  const target = options.target ?? window
  const bridgeRegistry = target as unknown as Record<string, unknown>
  const bridgeName =
    typeof options.bridgeName === 'string' && options.bridgeName.trim()
      ? options.bridgeName.trim()
      : FIREBASE_WATCHDOG_BRIDGE_NAMES.DEFAULT
  const exposePageBridge = options.exposePageBridge !== false
  const exposeLegacyDemoAlias = options.exposeLegacyDemoAlias === true
  const probeIntervalMs = Number.isFinite(options.probeIntervalMs)
    ? Number(options.probeIntervalMs)
    : TIMER_MS.DEFAULT_PROBE_INTERVAL
  const probeTimeoutMs = Number.isFinite(options.probeTimeoutMs)
    ? Number(options.probeTimeoutMs)
    : TIMER_MS.DEFAULT_PROBE_TIMEOUT

  let disposed = false
  let monitoringStarted = false
  let probeInFlight = false
  let probeTimer: ReturnType<typeof setTimeout> | null = null
  let lastConnectionDetail = ''
  let lastMonitorDetail = ''

  const dispatchConnection = (connected: boolean) => {
    dispatchMonitor(true, FIREBASE_WATCHDOG_SOURCE_NAMES.FIRESTORE)
    lastConnectionDetail = JSON.stringify({
      connected,
      source: FIREBASE_WATCHDOG_SOURCE_NAMES.FIRESTORE,
      timestamp: Date.now(),
    })

    target.dispatchEvent(
      new CustomEvent(FIREBASE_WATCHDOG_EVENT_NAMES.CONNECTION, {
        detail: lastConnectionDetail,
      })
    )
  }

  const dispatchMonitor = (available: boolean, source: string) => {
    lastMonitorDetail = JSON.stringify({
      available,
      source,
      timestamp: Date.now(),
    })

    target.dispatchEvent(
      new CustomEvent(FIREBASE_WATCHDOG_EVENT_NAMES.MONITOR, {
        detail: lastMonitorDetail,
      })
    )
  }

  const scheduleNextProbe = () => {
    if (probeTimer) {
      clearTimeout(probeTimer)
      probeTimer = null
    }

    if (disposed || !monitoringStarted) {
      return
    }

    probeTimer = setTimeout(() => {
      void runProbe()
    }, probeIntervalMs)
  }

  const runProbe = async () => {
    if (disposed || !monitoringStarted || probeInFlight) {
      return
    }

    probeInFlight = true

    try {
      if (navigator.onLine === false) {
        dispatchConnection(false)
        return
      }

      dispatchMonitor(true, FIREBASE_WATCHDOG_SOURCE_NAMES.FIRESTORE)

      await withTimeout(
        getDocFromServer(options.healthCheckRef),
        probeTimeoutMs
      )

      if (!disposed) {
        dispatchConnection(true)
      }
    } catch (error) {
      if (isConnectivityError(error)) {
        dispatchConnection(false)
      } else {
        dispatchMonitor(false, FIREBASE_WATCHDOG_SOURCE_NAMES.FIRESTORE_PROBE_ERROR)
      }
    } finally {
      probeInFlight = false
      scheduleNextProbe()
    }
  }

  const startMonitoring = () => {
    if (disposed || monitoringStarted) {
      return
    }

    monitoringStarted = true
    dispatchMonitor(true, FIREBASE_WATCHDOG_SOURCE_NAMES.FIRESTORE)
    void runProbe()
  }

  const handleRequestState = () => {
    startMonitoring()

    if (lastMonitorDetail) {
      target.dispatchEvent(
        new CustomEvent(FIREBASE_WATCHDOG_EVENT_NAMES.MONITOR, {
          detail: lastMonitorDetail,
        })
      )
    }

    if (lastConnectionDetail) {
      target.dispatchEvent(
        new CustomEvent(FIREBASE_WATCHDOG_EVENT_NAMES.CONNECTION, {
          detail: lastConnectionDetail,
        })
      )
    }
  }

  const handleOnline = () => {
    if (monitoringStarted) {
      void runProbe()
    }
  }

  const handleOffline = () => {
    if (monitoringStarted) {
      dispatchConnection(false)
    }
  }

  const bridge = createFirestoreWatchdogBridge({
    onGoOffline: async () => {
      await disableNetwork(firestore)
      dispatchConnection(false)
    },
    onGoOnline: async () => {
      await enableNetwork(firestore)
      if (monitoringStarted) {
        await runProbe()
      }
    },
    onReconnect: async () => {
      await disableNetwork(firestore)
      await wait(TIMER_MS.RECONNECT_SETTLE)
      await enableNetwork(firestore)
      if (monitoringStarted) {
        await runProbe()
      }
    },
  })

  target.addEventListener(
    FIREBASE_WATCHDOG_EVENT_NAMES.REQUEST_STATE,
    handleRequestState
  )
  target.addEventListener('online', handleOnline)
  target.addEventListener('offline', handleOffline)

  if (exposePageBridge) {
    bridgeRegistry[bridgeName] = bridge

    if (exposeLegacyDemoAlias) {
      bridgeRegistry[FIREBASE_WATCHDOG_BRIDGE_NAMES.LEGACY_DEMO_ALIAS] = bridge
    }
  }

  return () => {
    disposed = true

    if (probeTimer) {
      clearTimeout(probeTimer)
      probeTimer = null
    }

    target.removeEventListener(
      FIREBASE_WATCHDOG_EVENT_NAMES.REQUEST_STATE,
      handleRequestState
    )
    target.removeEventListener('online', handleOnline)
    target.removeEventListener('offline', handleOffline)

    if (exposePageBridge && bridgeRegistry[bridgeName] === bridge) {
      delete bridgeRegistry[bridgeName]
    }

    if (
      exposeLegacyDemoAlias &&
      bridgeRegistry[FIREBASE_WATCHDOG_BRIDGE_NAMES.LEGACY_DEMO_ALIAS] === bridge
    ) {
      delete bridgeRegistry[FIREBASE_WATCHDOG_BRIDGE_NAMES.LEGACY_DEMO_ALIAS]
    }
  }
}

function createFirestoreWatchdogBridge({
  onGoOffline,
  onGoOnline,
  onReconnect,
}: {
  onGoOffline: () => Promise<void>
  onGoOnline: () => Promise<void>
  onReconnect: () => Promise<void>
}): FirestoreWatchdogBridge {
  return Object.freeze({
    goOffline() {
      return onGoOffline()
    },
    goOnline() {
      return onGoOnline()
    },
    reconnect() {
      return onReconnect()
    },
  })
}

function isConnectivityError(error: unknown) {
  const errorCode = getErrorCode(error)
  const errorMessage = getErrorMessage(error).toLowerCase()

  if (
    errorCode === ERROR_CODES.CANCELLED ||
    errorCode === ERROR_CODES.DEADLINE_EXCEEDED ||
    errorCode === ERROR_CODES.UNAVAILABLE
  ) {
    return true
  }

  return (
    errorMessage.includes('offline') ||
    errorMessage.includes('network') ||
    errorMessage.includes('timed out')
  )
}

function getErrorCode(error: unknown) {
  if (error && typeof error === 'object' && 'code' in error) {
    return String(error.code)
  }

  return ''
}

function getErrorMessage(error: unknown) {
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message)
  }

  return String(error)
}

function wait(durationMs: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, durationMs)
  })
}

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number) {
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error(`Firestore watchdog probe timed out after ${timeoutMs}ms.`))
        }, timeoutMs)
      }),
    ])
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
  }
}
